import { useEffect, useMemo, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { AuthBackground } from "@/components/auth/AuthBackground";
import { AuthSidePanel } from "@/components/auth/AuthSidePanel";
import {
  pickRandomVariant,
  signUpVariants,
} from "@/components/auth/authVariants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { DemoGenieCloneService } from "@/services/demo/DemoGenieCloneService";
import { getDiscoverAttribution } from "@/lib/discover-attribution";
import { getLandingAttribution } from "@/lib/landingAttribution";
import { trackEvent } from "@/lib/ga4";
import { capturePrivateAccessToken } from "@/lib/privateAccessToken";
import { UserService } from "@/services/users/UserService";

type AccountType = "consumer" | "business";

export default function SignUp() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { signUp, signIn } = useAuth();

  // Always default to business account type
  const [accountType] = useState<AccountType>("business");

  // Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const teamId = searchParams.get("team_id");
  const inviteEmail = searchParams.get("email");
  const invitationId = useMemo(
    () => teamId && inviteEmail ? capturePrivateAccessToken() : undefined,
    [inviteEmail, teamId],
  );

  // Landing pages send `helpgenie.ai/signup?url=theirsite.com` so the genie
  // gets built from the visitor's own site. Carried through to
  // /welcome/business, never dropped on the floor.
  const prefillWebsite = searchParams.get("url") ?? searchParams.get("website");
  // Shown back to the visitor so they know the site they typed is not lost
  // between the landing page and the build step.
  const prefillSiteLabel = useMemo(() => {
    if (!prefillWebsite) return null;
    const bare = prefillWebsite
      .trim()
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .replace(/\/+$/, "");
    return bare ? bare.slice(0, 60) : null;
  }, [prefillWebsite]);
  const welcomeBusinessRoute = prefillWebsite
    ? `/welcome/business?url=${encodeURIComponent(prefillWebsite)}`
    : "/welcome/business";

  // Check for pending demo clone
  const hasPendingClone = DemoGenieCloneService.hasPendingClone();

  // Pick a random marketing variant on mount — stays stable through re-renders
  const variant = useMemo(() => pickRandomVariant(signUpVariants), []);

  useEffect(() => {
    if (inviteEmail) {
      setEmail(inviteEmail);
    }
  }, [inviteEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (
      (teamId || inviteEmail) &&
      (!teamId || !inviteEmail || !invitationId)
    ) {
      setError("Invalid invitation link.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      const finalEmail = inviteEmail || email;
      console.log("Checking if user exists:", { email: finalEmail });

      const { data, error } = await signIn(finalEmail, password);

      if (data.user && !error) {
        console.log("User exists and credentials are correct, signing in");
        if (teamId && invitationId) {
          await UserService.acceptTeamInvitation(teamId, invitationId);
          await queryClient.invalidateQueries({
            queryKey: ["auth", "userData"],
          });
        }
        // Check for pending clone after sign in
        if (hasPendingClone) {
          navigate("/creating-genie", { replace: true });
        } else {
          navigate("/dashboard");
        }
      } else {
        console.log("User does not exist, proceeding with signup");
        // signUp() also merges the full attribution (buildSignupMetadata:
        // last touch, CTA, visitor and GA4 ids, _gcl_aw) under this.
        const landingAttribution = getLandingAttribution();
        await signUp(
          finalEmail,
          password,
          accountType,
          teamId || undefined,
          fullName,
          invitationId,
          landingAttribution,
        );

        trackEvent("sign_up", {
          method: "email",
          account_type: accountType,
          source: teamId
            ? "team_invite"
            : hasPendingClone
              ? "pending_clone"
              : "direct",
          ...landingAttribution,
        });
        if (hasPendingClone) {
          const pendingClone = DemoGenieCloneService.getPendingClone();
          trackEvent("signup_completed", {
            method: "email",
            persona_id: pendingClone?.persona_id,
            ...getDiscoverAttribution(),
          });
        }

        if (hasPendingClone) {
          navigate("/creating-genie", { replace: true });
          return;
        } else if (teamId) {
          // Joining an existing team's genie(s) — the dashboard is right, not
          // a fresh build-a-genie wizard.
          navigate("/dashboard");
        } else {
          // Fresh business signup, no genie yet — straight into the wizard
          // that builds one from their own website instead of the dashboard.
          navigate(welcomeBusinessRoute);
        }
      }
    } catch (err: any) {
      console.error("Error during user check/signup:", err);
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Logo - Top Left */}
      <a
        href="/"
        className="absolute top-8 left-8 z-20 hidden lg:inline-flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
      >
        <HelpGenieLogo size={32} />
        <div className="flex flex-col hidden sm:flex">
          <span className="font-bold text-lg text-white tracking-tight">
            Help Genie
          </span>
          <span className="text-xs text-gray-300 -mt-1">Now We're Talking</span>
        </div>
      </a>

      {/* Left side - Hero image background */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
        <AuthBackground />
        <AuthSidePanel variant={variant} />
      </div>

      {/* Right side - Sign up form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div>
            <img
              src="/helpgenie.png"
              alt="Help Genie"
              className="h-12 w-auto mb-6"
            />
            <h2 className="text-3xl font-bold text-[#222B45]">
              Create Account
            </h2>
            <p className="mt-2 text-[#6B7280]">
              {hasPendingClone
                ? "Create your account to get your genie"
                : teamId
                  ? "Join your team on Help Genie"
                  : "Free plan, no credit card. Your first genie is built from your website."}
            </p>
            {prefillSiteLabel && !hasPendingClone && !teamId && (
              <div
                className="mt-4 rounded-lg border border-[#4E9CFF]/30 bg-[#4E9CFF]/5 p-3"
                data-testid="signup-site-banner"
              >
                <p className="text-sm font-semibold text-[#222B45]">
                  Building a genie for{" "}
                  <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                    {prefillSiteLabel}
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-[#6B7280]">
                  Make your login and we start reading your site straight away.
                </p>
              </div>
            )}
            {hasPendingClone && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm text-green-800 font-medium">
                      Your genie is ready to be created!
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/signin")}
                  className="mt-2 w-full text-center text-sm font-medium text-genie-primary hover:text-[#3579cc] py-1.5 border border-genie-primary/20 rounded-md hover:bg-genie-primary/5 transition-colors"
                >
                  Already have an account? Sign in instead
                </button>
              </div>
            )}
            {teamId && inviteEmail && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm text-blue-800 font-medium">
                    You've been invited to join a team as <CopyableEmail email={inviteEmail} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <form
            name="helpgenie_signup"
            id="helpgenie_signup"
            data-hs-do-not-collect="true"
            onSubmit={handleSubmit}
            className="mt-8 space-y-6"
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full name</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  autoComplete="name"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={inviteEmail ? inviteEmail : "Enter your email"}
                  required
                  autoComplete="email"
                  disabled={!!inviteEmail}
                  className={`mt-1 ${inviteEmail ? "bg-gray-50 cursor-not-allowed" : ""}`}
                />
                {inviteEmail && (
                  <p className="text-xs text-gray-500 mt-1">
                    Email is pre-filled from your invitation
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                    minLength={6}
                    autoComplete="new-password"
                    className="pr-16"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-genie-primary hover:text-[#3579cc]"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-500 text-center p-3 bg-red-50 rounded-md">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold shadow-lg transition-all hover:from-purple-700 hover:to-blue-700 hover:-translate-y-0.5"
            >
              {loading ? "Creating Account..." : "Start Free"}
            </Button>

            <div className="text-center text-sm">
              <span className="text-[#6B7280]">Already have an account? </span>
              <button
                type="button"
                onClick={() => navigate("/signin")}
                className="text-genie-primary hover:text-[#3579cc] font-medium"
              >
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
